import { useEffect, useState, useRef, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  Image,
  Box,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  Flex,
} from '@chakra-ui/react';
import {
  captureApi,
  CaptureSessionType,
} from '@texas/api/endpoints/capture/captureApi';
import { useTranslation } from 'react-i18next';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { compareEnum, compareEnums } from '@texas/utils/helpers/enumHelpers';

interface CapturePhotoProps {
  referenceId: number;
  referenceType: CaptureSessionType;
  text1: string;
  text2?: string;
  isOpen: boolean;
  onClose: () => void;
  onPhotoReceived: (file: FileLink) => void;
}

enum CaptureState {
  Initializing,
  DisplayingQR,
  WaitingForPhoto,
  Success,
  Error,
}

export function CapturePhoto({
  referenceId,
  referenceType,
  text1,
  text2,
  isOpen,
  onClose,
  onPhotoReceived,
}: CapturePhotoProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      isCentered={true}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <Inner
          referenceId={referenceId}
          referenceType={referenceType}
          text1={text1}
          text2={text2}
          onClose={onClose}
          onPhotoReceived={onPhotoReceived}
        />
      </ModalContent>
    </Modal>
  );
}

type InnerProps = Omit<CapturePhotoProps, 'isOpen'>;

function Inner({
  referenceId,
  referenceType,
  text1,
  text2,
  onClose,
  onPhotoReceived,
}: InnerProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<CaptureState>(CaptureState.Initializing);
  const [qrCodeBase64, setQrCodeBase64] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [file, setFile] = useState<FileLink | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const stateRef = useRef<CaptureState>(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const poll = useCallback(
    async (sessionIdToUse: string) => {
      const statusResponse = await captureApi.getCaptureSessionStatus({
        referenceId,
        referenceType,
        sessionId: sessionIdToUse,
      });

      if (statusResponse.hasError) {
        return;
      }

      if (statusResponse.data.hasPhoto && statusResponse.data.file) {
        stopPolling();
        setFile(statusResponse.data.file);
        setState(CaptureState.Success);
      } else if (stateRef.current === CaptureState.DisplayingQR) {
        setState(CaptureState.WaitingForPhoto);
      }
    },
    [referenceId, referenceType],
  );

  const startPolling = useCallback(
    (sessionIdToUse: string) => {
      pollingIntervalRef.current = setInterval(() => {
        poll(sessionIdToUse);
      }, 2000);
    },
    [poll],
  );

  const initializeSession = useCallback(async () => {
    setState(CaptureState.Initializing);
    setError('');

    const response = await captureApi.initializeCaptureSession({
      referenceId,
      referenceType,
    });

    if (response.hasError) {
      setState(CaptureState.Error);
      setError(response.error.message);
      return;
    }

    setQrCodeBase64(response.data.qrCodeBase64);
    setState(CaptureState.DisplayingQR);
    startPolling(response.data.sessionId);
  }, [referenceId, referenceType, startPolling]);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const handleAccept = () => {
    if (file) {
      onPhotoReceived(file);
      handleClose();
    }
  };

  const handleClose = () => {
    stopPolling();
    setState(CaptureState.Initializing);
    setQrCodeBase64('');
    setError('');
    setFile(null);
    onClose();
  };

  const handleRetry = () => {
    initializeSession();
  };

  useEffect(() => {
    initializeSession();

    return () => {
      stopPolling();
    };
  }, [initializeSession]);

  const renderContent = () => {
    if (compareEnum(CaptureState, state, CaptureState.Initializing)) {
      return (
        <Flex direction="column" align="center" gap={4} py={8}>
          <Spinner size="xl" />
          <Text>{t('capturePhoto.generatingQrCode')}</Text>
        </Flex>
      );
    }

    if (
      compareEnums(CaptureState, state, [
        CaptureState.DisplayingQR,
        CaptureState.WaitingForPhoto,
      ])
    ) {
      return (
        <Flex direction="column" align="center" gap={4} py={4}>
          <Flex direction="column" align="center" gap={1} pb={2}>
            <Text fontSize="sm" fontWeight="bold">
              {text1}
            </Text>
            {text2 && (
              <Text fontSize="sm" color="gray.500">
                {text2}
              </Text>
            )}
          </Flex>
          <Text fontSize="lg" fontWeight="bold">
            {t('capturePhoto.scanQrCodeWithYourPhone')}
          </Text>
          <Text fontSize="sm" color="gray.300" textAlign="center" maxW="400px">
            {t('capturePhoto.openCameraAndScan')}
          </Text>
          <Box>
            <Image
              src={`data:image/png;base64,${qrCodeBase64}`}
              alt="QR Code"
              boxSize="300px"
            />
          </Box>
          {state === CaptureState.WaitingForPhoto && (
            <Flex direction="column" align="center" gap={2} pt={4}>
              <Spinner />
              <Text fontSize="sm" color="blue.600">
                {t('capturePhoto.waitingForPhoto')}
              </Text>
            </Flex>
          )}
          <Button variant="ghost" onClick={handleClose} mt={4}>
            {t('general.cancel')}
          </Button>
        </Flex>
      );
    }

    if (compareEnum(CaptureState, state, CaptureState.Success)) {
      return (
        <Flex direction="column" align="center" gap={4} py={4}>
          <Text fontSize="lg" fontWeight="bold" color="green.600">
            {t('capturePhoto.photoReceived')}
          </Text>
          <Box borderRadius="md" overflow="hidden" boxShadow="md">
            <Image
              src={`/api/files/${file?.identifier}/preview/512`}
              alt="Captured photo"
              maxH="400px"
            />
          </Box>
          <Button colorScheme="blue" onClick={handleAccept} w="full">
            {t('capturePhoto.acceptPhoto')}
          </Button>
          <Button variant="ghost" onClick={handleClose}>
            {t('general.cancel')}
          </Button>
        </Flex>
      );
    }

    if (compareEnum(CaptureState, state, CaptureState.Error)) {
      return (
        <Flex direction="column" align="center" gap={4} py={4}>
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>{error || 'An error occurred'}</AlertDescription>
          </Alert>
          <Button colorScheme="blue" onClick={handleRetry}>
            {t('general.retry')}
          </Button>
        </Flex>
      );
    }
  };

  return <ModalBody pb={6}>{renderContent()}</ModalBody>;
}
